import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import Clients from './pages/Clients';
import Modules from './pages/Modules';
import Roles from './pages/Roles';
import Users from './pages/Users';
import Attendance from './pages/Attendance';
import ActivityLogs from './pages/ActivityLogs';
import Holidays from './pages/Holidays';
import SiteExecution from './pages/SiteExecution';
import PurchaseManagement from './pages/PurchaseManagement';
import TaskPlanner from './pages/TaskPlanner';
import QualityCheckMgmt from './pages/QualityCheckMgmt';
import QuotationMgmt from './pages/QuotationMgmt';
import CustomerQuotationMgmt from './pages/CustomerQuotationMgmt';
import CustomerInvoiceMgmt from './pages/CustomerInvoiceMgmt';
import MaterialPlanning from './pages/MaterialPlanning';
import FinancePayment from './pages/FinancePayment';
import MailLogs from './pages/MailLogs';
import Reports from './pages/Reports';
import StockMaster from './pages/StockMaster';
import CustomerOrders from './pages/CustomerOrders';
import Workshop from './pages/Workshop';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontSize: '1.2rem', color: '#888' }}>Loading...</div>;
  return user ? children : <Navigate to="/login" />;
}

const MODULE_ROUTE_ORDER = [
  { path: '/dashboard', moduleKey: 'dashboard' },
  { path: '/projects', moduleKey: 'projects' },
  { path: '/clients', moduleKey: 'clients' },
  { path: '/quotations', moduleKey: 'quotations' },
  { path: '/customer-quotations', moduleKey: 'customer-quotations' },
  { path: '/customer-invoices', moduleKey: 'customer-invoices' },
  { path: '/material-planning', moduleKey: 'material-planning' },
  { path: '/purchase', moduleKey: 'purchase' },
  { path: '/customer-orders', moduleKey: 'customer-orders' },
  { path: '/workshop', moduleKey: 'workshop-jobs' },
  { path: '/task-planner', moduleKey: 'task-planner' },
  { path: '/site-execution', moduleKey: 'site-execution' },
  { path: '/quality-check', moduleKey: 'quality-check' },
  { path: '/finance', moduleKey: 'finance' },
  { path: '/attendance', moduleKey: 'attendance' },
  { path: '/reports', moduleKey: 'reports' },
  { path: '/users', moduleKey: 'users' },
  { path: '/roles', moduleKey: 'roles' },
  { path: '/modules', moduleKey: 'modules' },
  { path: '/activity-logs', moduleKey: 'activity-logs' },
  { path: '/mail-logs', moduleKey: 'mail-logs' },
  { path: '/masters/stock', moduleKey: 'stock-items' },
];

function useDefaultRoute() {
  const { canAccess, loading } = useAuth();
  if (loading) return null;
  const match = MODULE_ROUTE_ORDER.find(r => canAccess(r.moduleKey));
  return match ? match.path : '/holidays';
}

function ModuleRoute({ moduleKey, children }) {
  const { user, loading, canAccess } = useAuth();
  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontSize: '1.2rem', color: '#888' }}>Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  if (!canAccess(moduleKey)) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: '0.75rem', color: '#64748b' }}>
      <span style={{ fontSize: '2.5rem' }}>🔒</span>
      <h2 style={{ margin: 0, color: '#0f172a' }}>Access Denied</h2>
      <p style={{ margin: 0 }}>You don't have permission to view this module.</p>
    </div>
  );
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const defaultRoute = useDefaultRoute();

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontSize: '1.2rem', color: '#888' }}>Loading...</div>;

  return (
    <Routes>
      <Route path="/login"    element={user ? <Navigate to={defaultRoute} /> : <Login />} />
      <Route path="/register" element={user ? <Navigate to={defaultRoute} /> : <Register />} />

      <Route path="/dashboard" element={<ModuleRoute moduleKey="dashboard"><Dashboard /></ModuleRoute>} />

      {/* Module-gated routes */}
      <Route path="/projects"         element={<ModuleRoute moduleKey="projects"><Projects /></ModuleRoute>} />
      <Route path="/clients"          element={<ModuleRoute moduleKey="clients"><Clients /></ModuleRoute>} />
      <Route path="/quotations"       element={<ModuleRoute moduleKey="quotations"><QuotationMgmt /></ModuleRoute>} />
      <Route path="/customer-quotations" element={<ModuleRoute moduleKey="customer-quotations"><CustomerQuotationMgmt /></ModuleRoute>} />
      <Route path="/customer-invoices" element={<ModuleRoute moduleKey="customer-invoices"><CustomerInvoiceMgmt /></ModuleRoute>} />
      <Route path="/material-planning"element={<ModuleRoute moduleKey="material-planning"><MaterialPlanning /></ModuleRoute>} />
      <Route path="/purchase"         element={<ModuleRoute moduleKey="purchase"><PurchaseManagement /></ModuleRoute>} />
      <Route path="/customer-orders"  element={<ModuleRoute moduleKey="customer-orders"><CustomerOrders /></ModuleRoute>} />
      <Route path="/workshop"         element={<ModuleRoute moduleKey="workshop-jobs"><Workshop /></ModuleRoute>} />
      <Route path="/task-planner"     element={<ModuleRoute moduleKey="task-planner"><TaskPlanner /></ModuleRoute>} />
      <Route path="/site-execution"   element={<ModuleRoute moduleKey="site-execution"><SiteExecution /></ModuleRoute>} />
      <Route path="/quality-check"    element={<ModuleRoute moduleKey="quality-check"><QualityCheckMgmt /></ModuleRoute>} />
      <Route path="/finance"          element={<ModuleRoute moduleKey="finance"><FinancePayment /></ModuleRoute>} />
      <Route path="/attendance"       element={<ModuleRoute moduleKey="attendance"><Attendance /></ModuleRoute>} />
      <Route path="/reports"          element={<ModuleRoute moduleKey="reports"><Reports /></ModuleRoute>} />

      {/* Admin-only routes */}
      <Route path="/users"          element={<ModuleRoute moduleKey="users"><Users /></ModuleRoute>} />
      <Route path="/roles"          element={<ModuleRoute moduleKey="roles"><Roles /></ModuleRoute>} />
      <Route path="/modules"        element={<ModuleRoute moduleKey="modules"><Modules /></ModuleRoute>} />
      <Route path="/activity-logs"  element={<ModuleRoute moduleKey="activity-logs"><ActivityLogs /></ModuleRoute>} />
      <Route path="/mail-logs"      element={<ModuleRoute moduleKey="mail-logs"><MailLogs /></ModuleRoute>} />
      <Route path="/masters/stock"  element={<ModuleRoute moduleKey="stock-items"><StockMaster /></ModuleRoute>} />

      {/* Always accessible when logged in */}
      <Route path="/holidays" element={<PrivateRoute><Holidays /></PrivateRoute>} />

      <Route path="*" element={<Navigate to={defaultRoute} />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <ToastContainer position="top-right" autoClose={3000} />
      </BrowserRouter>
    </AuthProvider>
  );
}
